import React from 'react';

import { captureMessage } from '@sentry/react';
import type { AxiosError } from 'axios';

import { isAclosetError } from '@looko-corp/acloset-react-kit';
import { acCaptureError } from '@looko-corp/acloset-react-kit/src/sentry';

import axiosInstance from '../axios';

/**
 * 네트워크 오류 관찰자.
 */
export const useNetworkErrorObserverEffect = () => {
  React.useEffect(() => {
    try {
      const id = axiosInstance.interceptors.response.use(undefined, (err: AxiosError) => {
        if (isAclosetError(err)) {
          if ('code' in err && err.code === 'NOT_ALLOWED_METHOD') {
            return Promise.reject(err);
          }
        }

        const { response } = err;
        const errStatus = response?.status;

        if (!response || errStatus == null) {
          return Promise.reject(err);
        }

        const isError500 = errStatus >= 500 && errStatus < 600;

        const errorCode =
          response.data != null &&
          typeof response.data === 'object' &&
          'code' in response.data &&
          typeof response.data.code === 'string'
            ? response.data.code
            : '';

        if (errStatus === 400 || errStatus === 409 || isError500) {
          const errorMessage =
            response.data != null &&
            typeof response.data === 'object' &&
            'message' in response.data &&
            typeof response.data.message === 'string'
              ? response.data.message
              : '';

          if (errorCode) {
            if (err.config?.aclosetErrorHandler && err.config.aclosetErrorHandler[errorCode] != null) {
              // 핸들링 되는 에러는 무시
            } else {
              console.warn('에러 핸들링을 구현해주세요!', {
                method: err.config?.method,
                url: err.config?.url,
                code: errorCode,
                message: errorMessage,
              });
              captureMessage('에러 핸들링을 구현해주세요!', {
                level: 'info',
                extra: {
                  method: err.config?.method,
                  url: err.config?.url,
                  code: errorCode,
                  message: errorMessage,
                  request_id: err.config?.headers?.request_id,
                },
                contexts: {
                  response: {
                    headers: response.headers as Record<string, string>,
                    status_code: errStatus,
                  },
                },
              });
            }
            return Promise.reject(err);
          }

          acCaptureError(`Server error ${isError500 ? '5xx' : '400'}`, {
            fingerprint: ['looko-ai', 'web', _APP_VERSION, `server-error-${isError500 ? '500' : '400'}`],
            extra: {
              method: err.config?.method,
              url: err.config?.url,
              code: errorCode,
              message: errorMessage,
              request_id: err.config?.headers?.request_id,
            },
            contexts: {
              response: {
                headers: response.headers as Record<string, string>,
                status_code: errStatus,
              },
            },
          });
        }
        return Promise.reject(err);
      });

      return () => axiosInstance.interceptors.response.eject(id);
    } catch (err) {
      // ignore
      return undefined;
    }
  }, []);
};
